import { isFeatureEnabled } from './isFeatureEnabled';

/**
 * Where `?visualRefresh=on|off` is persisted, so the override survives the rest of the session and
 * later visits. Read and written only by this module — nothing in `index.html` or `public/` looks
 * at it.
 *
 * Per ORIGIN, being `localStorage`: turning the refresh on at `localhost` does not carry to a
 * preview URL, and each host needs its own `?visualRefresh=on` once.
 */
export const VISUAL_REFRESH_STORAGE_KEY = 'natter-visual-refresh';

/** `?visualRefresh=on` / `?visualRefresh=off`. */
export const VISUAL_REFRESH_QUERY_PARAM = 'visualRefresh';

/**
 * The class that goes on `<html>` when the refresh is active, so the two `font-family` declarations
 * CSS owns can follow the flag (HNS-260) — the `html` rule in `fonts/fonts.css` and the
 * `--font-sans` token in `components/styles/design-system.css`.
 *
 * Everything else is reached through the MUI theme, which switches in `common/theme`. Prefer that
 * seam: this class is for declarations no theme can write, and each new use of it is another place
 * the two halves of the switch can drift apart.
 */
export const VISUAL_REFRESH_CLASS = 'visual-refresh';

/**
 * Apply {@link VISUAL_REFRESH_CLASS} to the document. Called once from `src/index.tsx`, at module
 * scope, before `createRoot().render()`.
 *
 * That is early enough to avoid a flash of the wrong typeface without an inline `<head>` script:
 * the document this runs against is an empty `<div id="root">`, so there is no text on screen to
 * re-paint in a different face. It is NOT pre-parse — if a future use of this class ever styles
 * something the server-sent HTML renders, it would need moving into `index.html` to stay flash-free.
 *
 * `toggle` with an explicit second argument rather than `add`: `?visualRefresh=off` has to actively
 * REMOVE the class. It is the escape hatch for a user the refresh broke, and leaving a stale class
 * behind on a cached document would defeat it.
 */
export const applyVisualRefreshClass = (enabled: boolean) => {
  document.documentElement.classList.toggle(VISUAL_REFRESH_CLASS, enabled);
};

/**
 * `console.error` rather than `logger` on purpose, twice over: `common/services/logger` imports
 * `isFeatureEnabled` from `common/utils`, so using it here would close an import cycle back
 * through this barrel while it is still initialising — and this module evaluates before
 * `initDatadogLogs()` has run, so the logger would have nowhere to send it anyway.
 *
 * Safari in private mode throws on localStorage access rather than returning null, and this runs
 * during boot — a throw here would take the whole app down over a cosmetic flag.
 */
const readStoredOverride = (): boolean | undefined => {
  try {
    const stored = window.localStorage.getItem(VISUAL_REFRESH_STORAGE_KEY);
    return stored === null ? undefined : stored === 'true';
  } catch (error) {
    console.error('Could not read the stored visual-refresh override; falling back to the environment', error);
    return undefined;
  }
};

const writeStoredOverride = (enabled: boolean) => {
  try {
    window.localStorage.setItem(VISUAL_REFRESH_STORAGE_KEY, String(enabled));
  } catch (error) {
    console.error('Could not persist the visual-refresh override', error);
  }
};

/**
 * Resolve whether the Design System 2.0 visual refresh is active, in precedence order:
 *
 *   1. `?visualRefresh=on|off` — persisted, so it survives the rest of the session
 *   2. the persisted override from a previous visit
 *   3. `VITE_APP_ENABLE_VISUAL_REFRESH` — the environment default
 *
 * The override layer exists because the environment default is environment-WIDE: without it there
 * is no way to see the refresh on prod without shipping it to every customer, and no way for a
 * user whose session it breaks to get out before the next deploy. `off` is therefore as
 * first-class as `on` — support needs to be able to send one link.
 *
 * Exported for tests. Application code should read {@link VISUAL_REFRESH}, which calls this once.
 */
export const resolveVisualRefresh = (): boolean => {
  const param = new URLSearchParams(window.location.search).get(VISUAL_REFRESH_QUERY_PARAM);

  if (param === 'on' || param === 'off') {
    const enabled = param === 'on';
    writeStoredOverride(enabled);
    return enabled;
  }

  return readStoredOverride() ?? isFeatureEnabled('VisualRefresh');
};

/**
 * Whether the visual refresh is active for this session. Resolved ONCE, at module load.
 *
 * Deliberately a constant rather than a hook: the refresh switches the MUI theme factory and the
 * DS 2.0 `.dark` token layer together, and flipping it mid-session would leave the two disagreeing
 * for anything that has already mounted. Boot-time resolution also matches how `appSlice` reads
 * flags at module scope.
 */
export const VISUAL_REFRESH = resolveVisualRefresh();
